import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BarChart3, DollarSign, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";

const CYAN = "#00E5FF";
const COLORS = ["#00E5FF", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#6EE7B7"];

const tooltipStyle = { backgroundColor: "#0D1424", border: "1px solid rgba(255,255,255,0.2)", padding: "8px 12px", fontSize: "12px" };

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics/overview").then(r => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="p-8 text-slate-400">Loading analytics…</div>;

  const stageArr = Object.entries(data.stages).map(([stage, v]) => ({ stage: stage.replace(/_/g, " "), ...v }));
  const matchArr = Object.entries(data.match_distribution).map(([range, count]) => ({ range, count }));

  return (
    <div className="p-8 stagger-in" data-testid="analytics-page">
      <div className="mb-8">
        <div className="mono-label text-cyan-400 mb-2">// PIPELINE INTELLIGENCE</div>
        <h1 className="font-display font-black text-4xl tracking-tight">Analytics</h1>
        <p className="text-slate-400 mt-2">Value distribution, top agencies, deadline load</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <KPI label="Total Opportunities" value={data.total_opps} icon={BarChart3} />
        <KPI label="Pipeline Value" value={`$${(data.total_pipeline_value / 1e6).toFixed(2)}M`} icon={DollarSign} accent />
        <KPI label="Proposals" value={data.proposals_count} icon={TrendingUp} />
        <KPI label="Agencies" value={data.top_agencies.length} icon={BarChart3} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Panel title="Pipeline by Stage · $ value">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stageArr}>
              <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="stage" stroke="#64748B" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={70} />
              <YAxis stroke="#64748B" tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`$${Number(v).toLocaleString()}`, "value"]} />
              <Bar dataKey="value" fill={CYAN} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Match Score Distribution">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={matchArr} dataKey="count" nameKey="range" cx="50%" cy="50%" outerRadius={90} label={{ fill: "#94A0B8", fontSize: 11 }}>
                {matchArr.map((entry, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: "11px", color: "#94A0B8" }} />
            </PieChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Top Agencies by Opportunity Count">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.top_agencies} layout="vertical">
              <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.05)" />
              <XAxis type="number" stroke="#64748B" tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="agency" stroke="#64748B" tick={{ fontSize: 10 }} width={140} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" fill={CYAN} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Deadlines by Month">
          {data.monthly_deadlines.length === 0 ? <div className="text-center text-slate-500 py-16">No deadlines with dates yet</div> : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data.monthly_deadlines}>
                <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" stroke="#64748B" tick={{ fontSize: 10 }} />
                <YAxis stroke="#64748B" tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="count" stroke={CYAN} strokeWidth={2} dot={{ fill: CYAN, r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon, accent }) {
  return (
    <div className={`bg-[#0D1424] border ${accent ? "border-cyan-400/40" : "border-white/10"} p-6`}>
      <div className="flex items-center justify-between mb-4">
        <Icon className={`w-5 h-5 ${accent ? "text-cyan-400" : "text-slate-500"}`} strokeWidth={1.75} />
        <span className="mono-label text-slate-500">STAT</span>
      </div>
      <div className="font-display font-black text-3xl mb-1">{value}</div>
      <div className="text-xs text-slate-400">{label}</div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="bg-[#0D1424] border border-white/10 p-6">
      <div className="mono-label text-cyan-400 mb-4">{title}</div>
      {children}
    </div>
  );
}
